// ─── inyaneiUtils.js ────────────────────────────────────────────────────────
// Transformation logic: HalachicDayPayload → display items for InyaneiHaYom.
//
// Design principle: show only NOTEWORTHY information. Normal-state fields
// (tachanun said, no hallel, etc.) are omitted — the user doesn't need to be
// told about the default. Only deviations, additions, and special days appear.
// ─────────────────────────────────────────────────────────────────────────────

import { LIMUDIM_PROGRAMS } from '../../engine/providers/learningProvider';

// ═══════════════════════════════════════════════════════════════════════════════
// Category display order
// ═══════════════════════════════════════════════════════════════════════════════

export const CATEGORY_ORDER = [
  'seasonalPrayers',    // Mashiv HaRuach / Tal Umatar transitions — most time-sensitive
  'tachanun',           // Immediately affects tefillah structure
  'hallel',             // Hallel right after Shacharit Amidah
  'prayerAdditions',    // Ya'aleh V'Yavo, Al HaNisim — Amidah inserts
  'weekdayPrayers',     // Avinu Malkeinu, Kel Erech Apayim
  'selichot',           // Before Shacharit
  'shabbatAdditions',   // Shabbat-specific: Av HaRachamim, Mevarchim, etc.
  'motzaeiShabbat',     // Motzaei Shabbat additions
  'seasonalCustoms',    // L'David, Pirkei Avot, Eruv Tavshilin, Omer
  'liturgy',            // Musaf, Yizkor, Sifrei Torah, Kiddush Levana
  'mourningPeriod',     // Bein HaMetzarim, Nine Days
  'leyning',            // Torah reading & Haftarah
  'limudim',            // Daily learning programs (Daf Yomi, Mishna, Rambam, etc.)
];

// ═══════════════════════════════════════════════════════════════════════════════
// Category → icon mapping (lucide-react icon names)
// ═══════════════════════════════════════════════════════════════════════════════

const CATEGORY_ICONS = {
  tachanun:          'HandMetal',
  hallel:            'Music',
  prayerAdditions:   'Plus',
  seasonalPrayers:   'CloudRain',
  selichot:          'HeartHandshake',
  weekdayPrayers:    'BookOpen',
  shabbatAdditions:  'Star',
  motzaeiShabbat:    'MoonStar',
  seasonalCustoms:   'CalendarDays',
  liturgy:           'ScrollText',
  mourningPeriod:    'Flame',
  leyning:           'BookOpenText',
  limudim:           'GraduationCap',
};

/**
 * Get the lucide-react icon name for a given category.
 * @param {string} category
 * @returns {string} icon name
 */
export function getCategoryIcon(category) {
  return CATEGORY_ICONS[category] || 'Info';
}

// ═══════════════════════════════════════════════════════════════════════════════
// Item factory
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create a standardized display item.
 * @param {string}      category
 * @param {string}      label
 * @param {string}      value
 * @param {object}      [opts]
 * @param {string|null} [opts.detail]
 * @param {'high'|'normal'|'low'} [opts.priority]
 * @param {boolean}     [opts.isApplicable]
 * @returns {object}
 */
function makeItem(category, label, value, opts = {}) {
  return {
    category,
    icon: getCategoryIcon(category),
    label,
    value,
    detail: opts.detail ?? null,
    priority: opts.priority ?? 'normal',
    isApplicable: opts.isApplicable ?? true,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main builder
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Convert a HalachicDayPayload into an array of display items.
 *
 * @param {object} payload  — HalachicDayPayload from HalachicEngine
 * @param {object} settings — user toggle settings
 * @param {'ashkenaz'|'sephardic'|'mizrahi'} edah
 * @returns {Array<object>} ordered display items
 */
export function buildHalachicItems(payload, settings, edah) {
  if (!payload) return [];

  const items = [];

  // ─── Seasonal Prayers & Transitions ────────────────────────────────────────
  if (settings.showSeasonalPrayers && payload.seasonalPrayers) {
    items.push(...buildSeasonalPrayers(payload.seasonalPrayers, edah));
  }
  if (settings.showSeasonalTransitions && payload.seasonalPrayers) {
    items.push(...buildSeasonalTransitions(payload.seasonalPrayers));
  }

  // ─── Tachanun ──────────────────────────────────────────────────────────────
  if (settings.showTachanun && payload.tachanun) {
    items.push(...buildTachanun(payload.tachanun));
  }

  // ─── Hallel ────────────────────────────────────────────────────────────────
  if (settings.showHallel && payload.hallel) {
    items.push(...buildHallel(payload.hallel));
  }

  // ─── Prayer Additions ──────────────────────────────────────────────────────
  if (settings.showPrayerAdditions && payload.prayerAdditions) {
    items.push(...buildPrayerAdditions(payload.prayerAdditions));
  }

  // ─── Weekday Prayers ───────────────────────────────────────────────────────
  if (settings.showWeekdayPrayers && payload.weekdayPrayers) {
    items.push(...buildWeekdayPrayers(payload.weekdayPrayers));
  }

  // ─── Selichot ──────────────────────────────────────────────────────────────
  if (settings.showSelichot && payload.selichot) {
    items.push(...buildSelichot(payload.selichot));
  }

  // ─── Shabbat Additions ─────────────────────────────────────────────────────
  if (settings.showShabbatAdditions && payload.shabbatAdditions) {
    items.push(...buildShabbatAdditions(payload.shabbatAdditions));
  }

  // ─── Motzaei Shabbat ──────────────────────────────────────────────────────
  if (settings.showMotzaeiShabbat && payload.motzaeiShabbat) {
    items.push(...buildMotzaeiShabbat(payload.motzaeiShabbat));
  }

  // ─── Seasonal Customs ──────────────────────────────────────────────────────
  if (settings.showSeasonalCustoms && payload.seasonalCustoms) {
    items.push(...buildSeasonalCustoms(payload.seasonalCustoms));
  }

  // ─── Liturgy ───────────────────────────────────────────────────────────────
  if (settings.showLiturgy && payload.liturgy) {
    items.push(...buildLiturgy(payload.liturgy));
  }

  // ─── Mourning Periods ──────────────────────────────────────────────────────
  if (settings.showMourningPeriods && payload.mourningPeriod) {
    items.push(...buildMourningPeriods(payload.mourningPeriod));
  }

  // ─── Leyning ──────────────────────────────────────────────────────────────
  if (settings.showLeyning && payload.leyning) {
    items.push(...buildLeyning(payload.leyning, settings));
  }

  // ─── Daily Learning Programs ─────────────────────────────────────────────
  if (settings.showLimudim && payload.limudim) {
    items.push(...buildLimudim(payload.limudim));
  }

  // Sort by CATEGORY_ORDER, preserving insertion order within each category
  return sortByCategory(items);
}

// ═══════════════════════════════════════════════════════════════════════════════
// Category builders — each returns an array of items (possibly empty)
// ═══════════════════════════════════════════════════════════════════════════════

// ─── Tachanun ────────────────────────────────────────────────────────────────
// Show only when tachanun is OMITTED (noteworthy). Normal days → skip.

function buildTachanun(tachanun) {
  const items = [];

  if (tachanun.shacharit === false) {
    const reason = tachanun.reason ? ` — ${tachanun.reason}` : '';
    const detail = tachanun.lamnatzeach === false ? 'ללא למנצח' : null;

    items.push(makeItem('tachanun', 'תחנון', `אין אומרים תחנון${reason}`, {
      detail,
    }));
  } else {
    // Tachanun is said (normal) — not noteworthy, don't show.
    // But if lamnatzeach is specifically false while tachanun is said, note it.
    if (tachanun.lamnatzeach === false) {
      items.push(makeItem('tachanun', 'למנצח', 'ללא למנצח', {
        priority: 'low',
      }));
    }
  }

  return items;
}

// ─── Hallel ──────────────────────────────────────────────────────────────────
// Show only when hallel is said. 'none' → skip (nothing noteworthy).

function buildHallel(hallel) {
  if (hallel.type === 'none') return [];

  const label = hallel.type === 'full' ? 'הלל שלם' : 'חצי הלל';
  const reason = hallel.reason ? ` — ${hallel.reason}` : '';

  return [makeItem('hallel', 'הלל', `${label}${reason}`)];
}

// ─── Prayer Additions ────────────────────────────────────────────────────────

function buildPrayerAdditions(pa) {
  const items = [];

  if (pa.yaalehVeyavo) {
    const reason = pa.yaalehVeyavoReason ? ` — ${pa.yaalehVeyavoReason}` : '';
    items.push(makeItem('prayerAdditions', 'יעלה ויבוא', `יעלה ויבוא${reason}`));
  }

  if (pa.alHanisim) {
    const reason = pa.alHanisimReason ? ` — ${pa.alHanisimReason}` : '';
    items.push(makeItem('prayerAdditions', 'על הניסים', `על הניסים${reason}`));
  }

  if (pa.anenu?.applicable) {
    const detail = formatAnenuDetail(pa.anenu);
    items.push(makeItem('prayerAdditions', 'עננו', 'עננו', { detail }));
  }

  if (pa.nachem) {
    items.push(makeItem('prayerAdditions', 'נחם', 'נחם — תשעה באב'));
  }

  return items;
}

/**
 * Format anenu detail string describing shacharit/mincha rules.
 */
function formatAnenuDetail(anenu) {
  const parts = [];
  if (anenu.shacharit === 'shatz_only') {
    parts.push('שחרית: ש״ץ בלבד');
  } else if (anenu.shacharit === 'everyone') {
    parts.push('שחרית: כולם');
  }
  if (anenu.mincha === 'everyone') {
    parts.push('מנחה: כולם');
  }
  if (anenu.isNidcheh) {
    parts.push('(צום נדחה)');
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

// ─── Seasonal Prayers ────────────────────────────────────────────────────────
// Always show current state of Mashiv HaRuach / Morid HaTal and
// Veten Tal Umatar / Veten Bracha.

function buildSeasonalPrayers(sp, edah) {
  const items = [];

  // Mashiv HaRuach vs Morid HaTal
  if (sp.mashivHaruach) {
    items.push(makeItem('seasonalPrayers', 'גשם', 'משיב הרוח ומוריד הגשם'));
  } else if (sp.moridHatal?.applicable) {
    if (sp.moridHatal.text === 'morid_hatal') {
      items.push(makeItem('seasonalPrayers', 'טל', 'מוריד הטל'));
    } else if (sp.moridHatal.text === 'omit') {
      // Ashkenaz diaspora: nothing is said — still show so user knows the state
      items.push(makeItem('seasonalPrayers', 'טל', 'ללא הזכרת גשם/טל', {
        priority: 'low',
      }));
    }
  }

  // Veten Tal Umatar vs Veten Bracha
  if (sp.vetenTalUmatar) {
    items.push(makeItem('seasonalPrayers', 'ברכת השנים', 'ותן טל ומטר'));
  } else if (sp.vetenBracha) {
    items.push(makeItem('seasonalPrayers', 'ברכת השנים', 'ותן ברכה'));
  }

  return items;
}

// ─── Seasonal Prayer Transitions (HIGH priority) ────────────────────────────

function buildSeasonalTransitions(sp) {
  const items = [];

  if (sp.mashivHaruachTransition?.isTransitionDay) {
    const t = sp.mashivHaruachTransition;
    if (t.startsAtMusaf) {
      items.push(makeItem('seasonalPrayers', 'שינוי נוסח', 'התחלת משיב הרוח ומוריד הגשם — במוסף', {
        priority: 'high',
        detail: 'מתחילים לומר "משיב הרוח ומוריד הגשם" במוסף',
      }));
    }
    if (t.endsAtMusaf) {
      items.push(makeItem('seasonalPrayers', 'שינוי נוסח', 'הפסקת משיב הרוח — במוסף', {
        priority: 'high',
        detail: 'מפסיקים לומר "משיב הרוח ומוריד הגשם" במוסף',
      }));
    }
  }

  if (sp.talUmatarTransition?.isTransitionDay) {
    if (sp.talUmatarTransition.startsAtMaariv) {
      items.push(makeItem('seasonalPrayers', 'שינוי נוסח', 'התחלת ותן טל ומטר — בערבית', {
        priority: 'high',
        detail: 'מתחילים לשאול "ותן טל ומטר לברכה" בערבית',
      }));
    }
  }

  return items;
}

// ─── Selichot ────────────────────────────────────────────────────────────────

function buildSelichot(selichot) {
  if (!selichot.applicable) return [];

  const reason = selichot.reason ? ` — ${selichot.reason}` : '';
  return [makeItem('selichot', 'סליחות', `סליחות${reason}`)];
}

// ─── Weekday Prayers ─────────────────────────────────────────────────────────

function buildWeekdayPrayers(wp) {
  const items = [];

  if (wp.avinuMalkeinu?.applicable) {
    const reason = wp.avinuMalkeinu.reason ? ` — ${wp.avinuMalkeinu.reason}` : '';
    items.push(makeItem('weekdayPrayers', 'אבינו מלכנו', `אבינו מלכנו${reason}`));
  }

  if (wp.kelErechApayim) {
    items.push(makeItem('weekdayPrayers', 'ק-ל ארך אפים', 'ק-ל ארך אפים', {
      priority: 'low',
    }));
  }

  return items;
}

// ─── Shabbat Additions ───────────────────────────────────────────────────────

function buildShabbatAdditions(sa) {
  const items = [];

  if (sa.avHarachamim) {
    items.push(makeItem('shabbatAdditions', 'אב הרחמים', 'אומרים אב הרחמים'));
  }

  if (sa.tzidkatcha) {
    items.push(makeItem('shabbatAdditions', 'צדקתך', 'אומרים צדקתך'));
  }

  if (sa.shabbatMevarchim) {
    const monthName = sa.mevarchimMonth || '';
    const moladDetail = formatMoladDetail(sa.mevarchimMolad);
    items.push(makeItem('shabbatAdditions', 'שבת מברכים', `שבת מברכים — ${monthName}`, {
      detail: moladDetail,
    }));
  }

  return items;
}

/**
 * Format molad details for Shabbat Mevarchim display.
 * @param {object|null} molad
 * @returns {string|null}
 */
function formatMoladDetail(molad) {
  if (!molad) return null;

  // Use the pre-rendered Hebrew text from @hebcal/core if available
  if (molad.renderedHe) {
    return `המולד: ${molad.renderedHe}`;
  }

  // Fallback to our own formatted string
  if (molad.formatted) {
    return `המולד: ${molad.formatted}`;
  }

  return null;
}

// ─── Motzaei Shabbat ─────────────────────────────────────────────────────────

function buildMotzaeiShabbat(ms) {
  const items = [];

  if (ms.vihiNoam) {
    items.push(makeItem('motzaeiShabbat', 'ויהי נועם', 'ויהי נועם'));
  }

  if (ms.vayitenLecha) {
    items.push(makeItem('motzaeiShabbat', 'ויתן לך', 'ויתן לך'));
  }

  return items;
}

// ─── Seasonal Customs ────────────────────────────────────────────────────────

function buildSeasonalCustoms(sc) {
  const items = [];

  if (sc.ledavidHashemOri) {
    items.push(makeItem('seasonalCustoms', 'לדוד ה׳ אורי', 'לדוד ה׳ אורי וישעי'));
  }

  if (sc.pirkeiAvot && sc.pirkeiAvotChapter) {
    items.push(makeItem('seasonalCustoms', 'פרקי אבות', `פרקי אבות — פרק ${formatHebrewChapter(sc.pirkeiAvotChapter)}`));
  }

  if (sc.barchiNafshi) {
    items.push(makeItem('seasonalCustoms', 'ברכי נפשי', 'ברכי נפשי'));
  }

  if (sc.eruvTavshilin) {
    items.push(makeItem('seasonalCustoms', 'עירוב תבשילין', 'יש להניח עירוב תבשילין', {
      priority: 'high',
      detail: 'יש להניח עירוב תבשילין לפני כניסת החג',
    }));
  }

  if (sc.isOmerSeason && sc.omerDay) {
    items.push(makeItem('seasonalCustoms', 'ספירת העומר', `ספירת העומר — יום ${sc.omerDay}`, {
      priority: 'low',
    }));
  }

  return items;
}

/**
 * Convert chapter number to Hebrew letter representation.
 * @param {number} n — 1-6
 * @returns {string}
 */
function formatHebrewChapter(n) {
  const CHAPTER_LETTERS = { 1: 'א׳', 2: 'ב׳', 3: 'ג׳', 4: 'ד׳', 5: 'ה׳', 6: 'ו׳' };
  return CHAPTER_LETTERS[n] || String(n);
}

// ─── Liturgy ─────────────────────────────────────────────────────────────────

function buildLiturgy(lit) {
  const items = [];

  if (lit.musafApplicable) {
    items.push(makeItem('liturgy', 'מוסף', 'תפילת מוסף'));
  }

  if (lit.yizkor) {
    items.push(makeItem('liturgy', 'יזכור', 'אומרים יזכור', {
      priority: 'high',
    }));
  }

  if (lit.sifreiTorah > 1) {
    const label = lit.sifreiTorah === 2 ? '2 ספרי תורה' : '3 ספרי תורה';
    items.push(makeItem('liturgy', 'ספרי תורה', label));
  }

  if (lit.kiddushLevana?.eligible) {
    const kl = lit.kiddushLevana;
    let dateRange = null;
    if (kl.earliestDate && kl.latestDate) {
      dateRange = `${kl.earliestDate} — ${kl.latestDate}`;
    }
    items.push(makeItem('liturgy', 'קידוש לבנה', 'קידוש לבנה', {
      detail: dateRange,
    }));
  }

  return items;
}

// ─── Mourning Periods ────────────────────────────────────────────────────────

function buildMourningPeriods(mp) {
  const items = [];

  // Show the most specific applicable mourning period.
  // The engine provides both isBeinHametzarim and isNineDays, with isNineDays
  // being a stricter subset of isBeinHametzarim. Show the note which already
  // contains the most specific description.
  if (mp.isNineDays) {
    items.push(makeItem('mourningPeriod', 'תשעת הימים', mp.note || 'תשעת הימים — ימי אבלות', {
      priority: 'high',
    }));
  } else if (mp.isBeinHametzarim) {
    items.push(makeItem('mourningPeriod', 'בין המצרים', mp.note || 'בין המצרים — שלושת השבועות', {
      priority: 'high',
    }));
  }

  return items;
}

// ─── Leyning ─────────────────────────────────────────────────────────────────

function buildLeyning(leyning, settings) {
  const items = [];

  // Torah reading — build Hebrew summary from aliyot data
  if (leyning.torah?.length > 0) {
    const summary = buildTorahSummaryHe(leyning.torah);
    if (summary) {
      items.push(makeItem('leyning', 'קריאת התורה', summary));
    }
  }

  // Haftarah
  if (settings.showHaftarah && leyning.haftaraHe) {
    const reason = leyning.reason ? ` (${leyning.reason})` : '';
    items.push(makeItem('leyning', 'הפטרה', `${leyning.haftaraHe}${reason}`));
  }

  return items;
}

/**
 * Build a Hebrew Torah reading summary from the aliyot array.
 * E.g. "ספר בראשית א:א - ו:ח" or "ספר במדבר כח:א - במדבר ל:א"
 * @param {Array} torah - Array of aliyot with bookHe, beginHe, endHe
 * @returns {string|null}
 */
function buildTorahSummaryHe(torah) {
  if (!torah || torah.length === 0) return null;
  const first = torah[0];
  const last = torah[torah.length - 1];
  const fBook = first.bookHe || first.book;
  const fBegin = first.beginHe || first.begin;
  const lEnd = last.endHe || last.end;
  const lBook = last.bookHe || last.book;

  if (fBook === lBook) {
    return `ספר ${fBook} ${fBegin} — ${lEnd}`;
  }
  return `ספר ${fBook} ${fBegin} — ${lBook} ${lEnd}`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Delta filter — for lookahead (Phase 5)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Filter a future day's items, keeping only those that differ from today.
 *
 * "Differ" means: the item has a different `value` for the same `label`,
 * or the item doesn't exist in today at all, or the item exists in today
 * but not in the future day (appeared/disappeared).
 *
 * This implements the "delta-only" display principle: if today is a regular
 * weekday and tomorrow is also a regular weekday with the same tachanun/
 * seasonal prayers, those items are omitted. Only new, changed, or high-
 * priority items make it through.
 *
 * @param {Array<object>} todayItems  — items from today (full list)
 * @param {Array<object>} futureItems — items from a future day (full list)
 * @returns {Array<object>} filtered future items (deltas only)
 */
export function filterLookaheadDeltas(todayItems, futureItems) {
  if (!todayItems || todayItems.length === 0) return futureItems || [];
  if (!futureItems || futureItems.length === 0) return [];

  // Build a lookup: label → value for today
  const todayMap = new Map();
  for (const item of todayItems) {
    todayMap.set(item.label, item.value);
  }

  return futureItems.filter((item) => {
    // Always show high-priority items (transitions, yizkor, eruv tavshilin, etc.)
    if (item.priority === 'high') return true;

    const todayValue = todayMap.get(item.label);

    // Item doesn't exist today → new item → show
    if (todayValue === undefined) return true;

    // Item exists today with different value → changed → show
    if (todayValue !== item.value) return true;

    // Same label and value as today → not noteworthy → skip
    return false;
  });
}

// ─── Daily Learning Programs ───────────────────────────────────────────────
// Show all active learning programs for the day.

function buildLimudim(limudim) {
  const items = [];
  const progMap = {};
  for (const p of LIMUDIM_PROGRAMS) progMap[p.key] = p;

  for (const [key, data] of Object.entries(limudim)) {
    const prog = progMap[key];
    if (!prog || !data) continue;
    items.push(makeItem('limudim', prog.he, data.name, { priority: 'low' }));
  }
  return items;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Sorting
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Sort items by CATEGORY_ORDER, then by priority within each category
 * (high first, then normal, then low).
 * Items within the same category+priority preserve their insertion order.
 *
 * @param {Array<object>} items
 * @returns {Array<object>}
 */
function sortByCategory(items) {
  const categoryIndex = {};
  CATEGORY_ORDER.forEach((cat, i) => { categoryIndex[cat] = i; });

  const priorityWeight = { high: 0, normal: 1, low: 2 };

  return [...items].sort((a, b) => {
    const catA = categoryIndex[a.category] ?? 999;
    const catB = categoryIndex[b.category] ?? 999;
    if (catA !== catB) return catA - catB;

    const priA = priorityWeight[a.priority] ?? 1;
    const priB = priorityWeight[b.priority] ?? 1;
    return priA - priB;
  });
}
